import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { FileDown } from "lucide-react";
import { useAppDispatch, useAppSelector } from "../../app/hooks";
import { selectIsOrgOwnerOrAdmin } from "../../features/authentication/authenticationSlice";
import { downloadTrainingRecord } from "../../features/training/slice-extensions/training-record-slice-extension";
import { ROUTES } from "../../routes";
import { RECORD_NOT_AVAILABLE } from "../../features/training/utils/trainingRecordErrors";

interface TrainingRecordButtonProps {
  memberId: string;
  enrolmentId: string;
}

/**
 * Downloads one enrolment's training record (Ausbildungsnachweis) PDF. When the school
 * has no business details yet (409) it says so and links Owner/Admin to the settings.
 */
const TrainingRecordButton: React.FC<TrainingRecordButtonProps> = ({ memberId, enrolmentId }) => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const canManage = useAppSelector(selectIsOrgOwnerOrAdmin);
  const [busy, setBusy] = useState(false);
  const [needsProfile, setNeedsProfile] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const download = async () => {
    setBusy(true);
    setError(null);
    setNeedsProfile(false);
    const result = await dispatch(downloadTrainingRecord({ memberId, enrolmentId }));
    setBusy(false);
    if (downloadTrainingRecord.fulfilled.match(result)) return;
    if (result.payload?.code === RECORD_NOT_AVAILABLE) {
      setError(t("training.record.notAvailable"));
    } else if (result.payload?.statusCode === 409) {
      setNeedsProfile(true);
    } else {
      setError(result.payload?.message ?? t("training.record.failed"));
    }
  };

  return (
    <div className="mt-1">
      <button
        type="button"
        className="inline-flex items-center gap-1 text-sm text-primary-40 dark:text-primary-90 hover:underline"
        disabled={busy}
        onClick={download}
      >
        <FileDown size={16} />
        <span>{t("training.record.download")}</span>
      </button>
      {needsProfile && (
        <p className="text-sm text-grey-50 dark:text-grey-70">
          {t("training.record.needsBusinessProfile")}{" "}
          {canManage && (
            <Link to={ROUTES.ORGANIZATION_SETTINGS} className="text-primary-40 dark:text-primary-90 hover:underline">
              {t("businessProfile.title")}
            </Link>
          )}
        </p>
      )}
      {error && <p className="text-sm text-red-600 dark:text-red-400">{error}</p>}
    </div>
  );
};

export default TrainingRecordButton;
